import React from 'react';

import {getScrollableParent} from 'app/utils/browser';

interface Props {
  minHeight?: number;
  children: (args: {
    lockStepHeight: (stepId: string) => void;
    unlockStepHeight: (stepId: string) => void;
    getStepLiRef: (stepId: string) => (ref: HTMLLIElement) => void;
  }) => JSX.Element;
}

export function StepsHeightManager({
  children,
  // 410 is the minimum height of ActiveEditableProcedureStep
  minHeight = 410,
}: Props): JSX.Element {
  const scrollableParentRef = React.useRef<HTMLElement | null>(null);
  const stepsRef = React.useRef<{[stepId: string]: HTMLLIElement}>({});

  function getStepsScrollableParent(stepContainer: HTMLLIElement) {
    // calculate it once
    if (scrollableParentRef.current == null) {
      scrollableParentRef.current = getScrollableParent(stepContainer);
    }
    return scrollableParentRef.current;
  }

  function isStepAboveScrollableParent(
    stepContainer: HTMLLIElement,
    stepTop: number
  ): boolean {
    const scrollableParentTop =
      getStepsScrollableParent(stepContainer).getBoundingClientRect().top;

    return stepTop < scrollableParentTop;
  }

  function lockStepHeight(stepId: string) {
    const stepRef = stepsRef.current[stepId];
    if (stepRef != null) {
      // fix the step container height
      const {height: stepHeight, top: stepTop} =
        stepRef.getBoundingClientRect();
      if (isStepAboveScrollableParent(stepRef, stepTop)) {
        stepRef.style.height = `${Math.max(
          minHeight,
          stepHeight
        ).toString()}px`;
      }
    }
  }

  function unlockStepHeight(stepId: string) {
    const stepRef = stepsRef.current[stepId];

    if (stepRef != null && stepRef.style.height !== '') {
      // release step container height
      stepRef.style.height = '';
    }
  }

  function getStepLiRef(stepId: string) {
    return function getStepRef(ref: HTMLLIElement) {
      stepsRef.current[stepId] = ref;
    };
  }

  return children({lockStepHeight, unlockStepHeight, getStepLiRef});
}
